import { Component, DestroyRef, OnInit, inject, output, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { GenericState } from 'src/app/store/generic.state';
import { JobAlertActions } from 'src/app/store/job-alert/job-alert.actions';
import { environment } from 'src/environments/environment';
import * as _ from 'underscore';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-job-alert-form',
    templateUrl: './job-alert-form.component.html',
    styleUrls: ['./job-alert-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, NzSpaceModule, NzInputModule, NzSelectModule, MatRadioGroup, MatRadioButton, NzGridModule, NzButtonModule]
})
export class JobAlertFormComponent implements OnInit {
	private store = inject(Store);
	private destroyRef = inject(DestroyRef);

	readonly data = input<any>();
	readonly dataList = input([]);
	readonly closeForm = output<any>();

	public form: any;
	public lookupCountries: any[] = [];
	public lookupCategories: any[] = [];
	public hasFormError = false;

	public errorMessage = '';

	private readonly genericLookup = this.store.select<any>(GenericState.getGeneric);

	public ngOnInit(): void {
		this.buildForm();

		this.genericLookup.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
			this.lookupCountries = data.lookUpCountries;
			this.lookupCategories = data.lookUpCategories;
		});

		this.patchForm();
	}

	private buildForm(): void {
		this.form = new FormGroup({
			id: new FormControl(0),
			keyword: new FormControl(''),
			location: new FormControl(''),
			category: new FormControl(''),
			isDaily: new FormControl(true),
			url: new FormControl(''),
			createdDate: new FormControl('')
		});
	}

	private patchForm(): void {
		let category: any[] = [];
		const dataCategoryArr = this.data().category.split(',');
		category = this.lookupCategories.filter((x: any) =>
			dataCategoryArr.includes(x)
		);

		this.form.patchValue({
			id: this.data().id,
			keyword: this.data().keyword,
			location: this.data().location,
			category: category,
			isDaily: this.data().isDaily ? 'true' : 'false',
			url: this.data().url,
			createdDate: this.data().createdDate
		});
	}

	private isDuplicate(formValue: any): boolean {
		const jobAlerts = this.dataList();

		const isDuplicate = jobAlerts.some((jobAlert: any) => {
			const frmKw = formValue.keyword ?? '';
			const frmLoc = formValue.location ?? '';
			const frmCat = formValue.category ?? '';

			const jaKw = jobAlert.keyword ?? '';
			const jaLoc = jobAlert.location ?? '';
			const jaCat = jobAlert.category ?? '';

			return (
				jaKw === frmKw &&
				jaLoc === frmLoc &&
				jaCat === frmCat &&
				jobAlert.id !== formValue.id
			);
		});

		return isDuplicate;
	}

	public onBlur(property: string) {
		let prop = this.form.get(property);
		prop?.setValue(prop?.value.sanitizeText());
		prop?.updateValueAndValidity();
	}

	public save(): void {
		const formValue = { ...this.form.value };

		formValue.category = formValue.category?.join(',');
		formValue.isDaily = formValue.isDaily === 'true';

		if (
			_.isEmpty(formValue.keyword) &&
			_.isEmpty(formValue.location) &&
			_.isEmpty(formValue.category)
		) {
			this.hasFormError = true;
			this.errorMessage =
				'Define keyword, location, and/or category to save this job alert.';
			return;
		}

		if (this.isDuplicate(formValue)) {
			this.hasFormError = true;
			this.errorMessage = 'Job alert already exists.';
			return;
		}

		const kw = formValue.keyword ? formValue.keyword : '';
		const loc = formValue.location ? formValue.location : '';
		const cat = formValue.category ? formValue.category : '';

		const baseTitle = `Your job alert`;
		const jobAlertKw = `for ${kw}`;
		const jobAlertLoc = `in ${loc}`;
		const jobAlertCat = `under ${cat}`;
		let jobAlertTitle = baseTitle;

		if (kw) jobAlertTitle += ` ${jobAlertKw}`;
		if (loc) jobAlertTitle += ` ${jobAlertLoc}`;
		if (cat) jobAlertTitle += ` ${jobAlertCat}`;

		const baseUrl = `${environment.baseClientUrl}/job-list?`;
		let uriEncoded = '';

		if (formValue.keyword)
			uriEncoded += `search=${encodeURIComponent(formValue.keyword)}`;
		if (formValue.location)
			uriEncoded += `&location=${encodeURIComponent(formValue.location)}`;
		if (formValue.category)
			uriEncoded += `&category=${encodeURIComponent(formValue.category)}`;

		const fullUrl = baseUrl + uriEncoded;

		const payload = {
			id: formValue.id,
			jobAlertName: jobAlertTitle,
			keyword: formValue.keyword,
			location: formValue.location,
			category: formValue.category,
			isDaily: formValue.isDaily,
			url: fullUrl,
			createdDate: formValue.createdDate
		};

		this.store.dispatch(new JobAlertActions.SaveJobAlert(payload));
		this.clearError();

		this.closeForm.emit(null);
	}

	public cancel(): void {
		this.clearError();
		this.closeForm.emit(null);
	}

	public clearError(): void {
		this.hasFormError = false;
		this.errorMessage = '';
	}
}
